import ClearIcon from "@mui/icons-material/Clear";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { ModalTransition } from "../../../../components/shared/Dialog/Config";
import JalaliDateTimePicker from "../../../../components/shared/jalali-datetimepicker";
import { formatLocalDateTimeForApi } from "../../../../middleware/middleware";

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const validationSchema = Yup.object({
  requestId: Yup.number().min(1, "درخواست معتبر نیست").required(),
  scheduledVisitTime: Yup.date()
    .required("زمان هماهنگ‌شده بازدید الزامی است.")
    .test(
      "not-before-today",
      "زمان بازدید نمی‌تواند قبل از امروز باشد.",
      (value) => {
        if (!value) return false;
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return false;
        return d.getTime() >= startOfToday().getTime();
      }
    ),
  description: Yup.string().required("لطفاً توضیحات را وارد کنید."),
  sendSms: Yup.boolean(),
});

const resolveFormAccess = (payload) => {
  const fa = payload?.formAccess || payload?.FormAccess;
  // اگر FormAccess از API نیاید، فیلدها را قفل نکن (لیست قبلاً canSchedule را فیلتر کرده).
  if (!fa || typeof fa !== "object") {
    return { canSetVisitDate: true, lockReason: "" };
  }
  const canSet =
    fa.canSetVisitDate ?? fa.CanSetVisitDate;
  return {
    canSetVisitDate: canSet == null ? true : Boolean(canSet),
    lockReason: fa.lockReason || fa.LockReason || "",
  };
};

const SetVisitDate = ({ open, onClose, id }) => {
  const [loading, setLoading] = useState(false);
  const [formAccess, setFormAccess] = useState({
    canSetVisitDate: true,
    lockReason: "",
  });
  const [initialData, setInitialData] = useState({
    requestId: 0,
    scheduledVisitTime: "",
    description: "",
    sendSms: true,
  });
  const minVisitDate = useMemo(() => startOfToday(), [open]);

  useEffect(() => {
    const fetchData = async () => {
      if (id && open) {
        try {
          setLoading(true);
          const res = await DefaultApiService.getOneApi(
            "SLN/Supervisor/SiteVisit",
            id
          );
          const payload = res?.data || res;
          const visit = payload?.siteVisit || payload?.SiteVisit;
          setFormAccess(resolveFormAccess(payload));
          if (visit) {
            setInitialData({
              requestId: visit.requestId || visit.RequestId || 0,
              scheduledVisitTime:
                visit.scheduledVisitTime &&
                !String(visit.scheduledVisitTime).startsWith("0001-01-01")
                  ? visit.scheduledVisitTime
                  : "",
              description: visit.description || "",
              sendSms: true,
            });
          }
        } catch (err) {
          toast.error(err?.response?.data?.message || "خطا در دریافت اطلاعات");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [id, open]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!formAccess.canSetVisitDate) {
      toast.error(formAccess.lockReason || "امکان تعیین زمان بازدید وجود ندارد.");
      setSubmitting(false);
      return;
    }

    try {
      setLoading(true);
      const res = await DefaultApiService.postApi(
        "SLN/Supervisor/SiteVisit/SetVisitDate",
        {
          requestId: values.requestId,
          scheduledVisitTime: formatLocalDateTimeForApi(
            values.scheduledVisitTime
          ),
          description: values.description,
          sendSms: values.sendSms !== false,
        }
      );
      if (res.isSuccess) {
        toast.success(res.message || "زمان بازدید با موفقیت ثبت شد.");
        resetForm();
        onClose(true);
      } else {
        toast.error(res.message || "خطا در ثبت زمان بازدید");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "خطا در ثبت تاریخ");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={ModalTransition}
      keepMounted
      onClose={() => onClose()}
      maxWidth="sm"
      fullWidth
      scroll="body"
      disableScrollLock
      PaperProps={{
        sx: {
          m: { xs: 1.5, sm: 2 },
          maxHeight: "calc(100% - 24px)",
          position: "relative",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Formik
        initialValues={initialData}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const {
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          } = formikProps;

          const readOnly = !formAccess.canSetVisitDate;

          return (
            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
            >
              <DialogContent
                dividers
                sx={{ overflowY: "auto", flex: "1 1 auto" }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <IconButton onClick={() => onClose()} size="small">
                      <ClearIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h5" fontWeight={600}>
                      هماهنگی زمان بازدید
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      زمان توافق‌شده با مشتری را ثبت کنید. پس از آن، از فرم
                      ویرایش، اطلاعات انجام بازدید را وارد می‌کنید.
                    </Typography>
                  </Grid>

                  {readOnly ? (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        {formAccess.lockReason ||
                          "در حال حاضر امکان تعیین زمان بازدید برای این پرونده وجود ندارد."}
                      </Alert>
                    </Grid>
                  ) : null}

                  <Grid item xs={12} sm={6}>
                    <JalaliDateTimePicker
                      label="زمان هماهنگ‌شده بازدید"
                      value={values.scheduledVisitTime}
                      disabled={readOnly}
                      minDate={minVisitDate}
                      onChange={(val) =>
                        setFieldValue("scheduledVisitTime", val)
                      }
                      error={
                        touched.scheduledVisitTime &&
                        Boolean(errors.scheduledVisitTime)
                      }
                      helperText={
                        touched.scheduledVisitTime && errors.scheduledVisitTime
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="توضیحات هماهنگی"
                      name="description"
                      value={values.description}
                      disabled={readOnly}
                      onChange={wrapFormikHandleChange(handleChange)}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      fullWidth
                      placeholder="مثلاً تماس با مشتری و توافق روی ساعت بازدید"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.sendSms !== false}
                          disabled={readOnly}
                          onChange={(e) =>
                            setFieldValue("sendSms", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="ارسال پیامک زمان بازدید به مشتری"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ flex: "0 0 auto" }}>
                <Button onClick={() => onClose()} variant="outlined">
                  انصراف
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formikProps.isSubmitting || loading || readOnly}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "ثبت زمان هماهنگ‌شده"
                  )}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default SetVisitDate;
